/**
 * BookingsPage — booking list + detail with submit/confirm/cancel actions.
 *
 * Route: `bookings` · RBAC: `booking:read` · Nav group: Hospitality.
 *
 * Bookings drive billing/PMS, so every action is NON-OPTIMISTIC and confirm/
 * cancel are gated by a confirmation Dialog (see BookingActions). The list is
 * server-filtered/searched/paginated via URL search params (toolbar +
 * pagination). Data flows through hooks; loading/empty/error are explicit.
 */

import { type BookingsFilters, useBookings } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  EmptyState,
  ErrorState,
  ListToolbar,
  LoadingState,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hostesa/ui';
import { CalendarCheck } from 'lucide-react';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import type { Booking } from './_deps';
import { BookingDetailPanel } from './booking-detail-panel';
import { BookingDraftCard } from './booking-draft-card';
import { namespace, resources } from './i18n';

/** Exposed for tests: register this slice's strings via renderWithProviders. */
export const bookingsI18nResources = {
  en: { [namespace]: resources.en },
  bg: { [namespace]: resources.bg },
  de: { [namespace]: resources.de },
  fr: { [namespace]: resources.fr },
} as const;

const STATUS_OPTIONS: Booking['status'][] = [
  'requested',
  'draft',
  'pending_staff_approval',
  'confirmed',
  'cancelled',
  'failed',
  'expired',
];

export interface BookingsPageProps {
  /** URL-driven server filter/search/pagination state. */
  filters?: BookingsFilters;
  /** Apply a new filter set (the route writes it to URL search params). */
  onFiltersChange?: (next: BookingsFilters) => void;
}

export function BookingsPage({ filters = {}, onFiltersChange }: BookingsPageProps = {}) {
  const { t } = useT(namespace);
  const { tenantId, hotelId } = useHotelContext();

  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;
  const bookingsQuery = useBookings(tenantId, hotelId, { ...filters, limit, offset });

  const bookings = bookingsQuery.data?.items ?? [];
  const total = bookingsQuery.data?.total ?? 0;
  const filterActive = Boolean(
    filters.status || filters.confirmationMode || filters.q || filters.from || filters.to,
  );
  const patch = (next: Partial<BookingsFilters>) => apply({ ...filters, ...next, offset: 0 });

  const [selected, setSelected] = useState<Booking | null>(null);
  const [open, setOpen] = useState(false);

  function openDetail(booking: Booking) {
    setSelected(booking);
    setOpen(true);
  }

  // Show the LIVE booking in the detail panel: after submit/confirm/cancel the
  // list query refetches, so re-read the selected booking from the fresh data
  // (falling back to the captured snapshot until the refetch lands).
  const liveSelected = selected ? (bookings.find((b) => b.id === selected.id) ?? selected) : null;

  return (
    <div data-testid="bookings-page" className="flex flex-col">
      <PageHeader
        data-testid="bookings-page-header"
        eyebrow={t('eyebrow')}
        title={t('title')}
        description={t('description')}
      />

      <ListToolbar
        data-testid="bookings-toolbar"
        searchValue={filters.q ?? ''}
        onSearchChange={(v) => patch({ q: v || undefined })}
        searchPlaceholder={t('toolbar.searchPlaceholder')}
        trailing={
          filterActive ? (
            <Button
              data-testid="bookings-clear-filters"
              variant="ghost"
              size="sm"
              onClick={() => apply({ limit })}
            >
              {t('toolbar.clear')}
            </Button>
          ) : undefined
        }
      >
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            patch({ status: v === 'all' ? undefined : (v as Booking['status']) })
          }
        >
          <SelectTrigger data-testid="bookings-filter-status" className="h-8 w-48">
            <SelectValue placeholder={t('toolbar.statusAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('toolbar.statusAll')}</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`bookings-filter-status-${s}`}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.confirmationMode ?? 'all'}
          onValueChange={(v) =>
            patch({ confirmationMode: v === 'all' ? undefined : (v as 'manual' | 'pms') })
          }
        >
          <SelectTrigger data-testid="bookings-filter-mode" className="h-8 w-36">
            <SelectValue placeholder={t('toolbar.modeAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('toolbar.modeAll')}</SelectItem>
            <SelectItem value="manual">{t('confirmationMode.manual')}</SelectItem>
            <SelectItem value="pms">{t('confirmationMode.pms')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label={t('toolbar.fromLabel')}
            data-testid="bookings-from"
            value={filters.from ?? ''}
            onChange={(e) => patch({ from: e.target.value || undefined })}
            className="h-8 w-40 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
          />
          <input
            type="date"
            aria-label={t('toolbar.toLabel')}
            data-testid="bookings-to"
            value={filters.to ?? ''}
            onChange={(e) => patch({ to: e.target.value || undefined })}
            className="h-8 w-40 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
          />
        </div>
      </ListToolbar>

      {bookingsQuery.isLoading ? (
        <LoadingState data-testid="bookings-loading" title={t('loading')} />
      ) : bookingsQuery.isError ? (
        <ErrorState
          data-testid="bookings-error"
          title={t('error.title')}
          description={t('error.description')}
          action={
            <Button
              data-testid="bookings-retry-btn"
              variant="secondary"
              size="sm"
              onClick={() => void bookingsQuery.refetch()}
            >
              {t('error.retry')}
            </Button>
          }
        />
      ) : bookings.length === 0 ? (
        filterActive ? (
          <EmptyState
            data-testid="bookings-empty-filtered"
            icon={<CalendarCheck />}
            title={t('emptyFiltered.title')}
            description={t('emptyFiltered.description')}
            action={
              <Button
                data-testid="bookings-clear-filters-empty"
                variant="secondary"
                size="sm"
                onClick={() => apply({ limit })}
              >
                {t('toolbar.clear')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            data-testid="bookings-empty"
            icon={<CalendarCheck />}
            title={t('empty.title')}
            description={t('empty.description')}
          />
        )
      ) : (
        <div className="flex flex-col gap-2" data-testid="bookings-list">
          {bookings.map((booking) => (
            <BookingDraftCard key={booking.id} booking={booking} onOpen={openDetail} />
          ))}
        </div>
      )}

      {!bookingsQuery.isLoading && !bookingsQuery.isError && total > 0 && (
        <Pagination
          data-testid="bookings-pagination"
          total={total}
          limit={limit}
          offset={offset}
          onChange={({ offset: nextOffset, limit: nextLimit }) =>
            apply({ ...filters, offset: nextOffset, limit: nextLimit })
          }
          rangeLabel={(from, to, t1) => t('paging.range', { from, to, total: t1 })}
          prevLabel={t('paging.prev')}
          nextLabel={t('paging.next')}
          pageSizeLabel={t('paging.perPage')}
        />
      )}

      <BookingDetailPanel
        booking={liveSelected}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSelected(null);
        }}
      />
    </div>
  );
}
